import { ApprovalAction } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ApprovalAssignment } from '~hooks/useApprovalsAssignments/models';
import { useApprovalsAssignments } from '~hooks/useApprovalsAssignments/useApprovalAssignments';
import connection from '~services/connectionModule';
import { PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type ApprovalInfo = {
  approvedAt?: Dayjs;
  approvedBy?: string;
  isApproved: boolean;
};

type ApprovalByRole<T extends string> = Partial<Record<T, ApprovalInfo>>;

type ApprovalsQueryContextType = {
  approvals: ApprovalByRole<string>;
  isLoading: boolean;
  refetchApprovals: () => Promise<void>;
};

const ApprovalsQueryContext = createContext<ApprovalsQueryContextType>({
  approvals: {},
  isLoading: true,
  refetchApprovals: async () => {},
});

interface ApprovalsQueryContextProviderProps {
  driverDayId: string;
}

function ApprovalsQueryContextProvider({
  children,
  driverDayId,
}: React.PropsWithChildren<ApprovalsQueryContextProviderProps>) {
  const { getApprovalAssignmentsByDriverDayId } = useApprovalsAssignments();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [approvals, setApprovals] = useState<ApprovalByRole<string>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchApprovals = useCallback(async () => {
    if (!companyId || !driverDayId) return;

    setIsLoading(true);

    try {
      let allApprovals: ApprovalAssignment[] = [];
      let shouldFetchNextPage = true;
      let paginationCursor: string | undefined;

      while (shouldFetchNextPage) {
        const approvalsResponse = await getApprovalAssignmentsByDriverDayId(
          companyId,
          driverDayId,
          'after' as PaginationLink,
          paginationCursor,
        );

        if (approvalsResponse?.data) {
          allApprovals = [...allApprovals, ...approvalsResponse.data];
          shouldFetchNextPage = Boolean(approvalsResponse.pagination?.after);
          paginationCursor = approvalsResponse.pagination?.after;
        } else {
          shouldFetchNextPage = false;
        }
      }

      if (allApprovals.length > 0) {
        const approvalsByType = allApprovals.reduce(
          (acc, approval) => {
            const approvalType = approval.approvalType as string;

            if (approvalType) {
              if (
                !acc[approvalType] ||
                dayjs
                  .tz(approval.createdAt)
                  .isAfter(dayjs.tz(acc[approvalType].createdAt))
              ) {
                acc[approvalType] = approval;
              }
            }

            return acc;
          },
          {} as Record<string, ApprovalAssignment>,
        );

        const approvalsByRole = Object.entries(approvalsByType).reduce(
          (obj, [approvalType, approval]) => {
            const isApproved = approval.approvalAction === ApprovalAction.APPROVE;

            if (isApproved) {
              obj[approvalType] = {
                isApproved: true,
                approvedBy: approval.externalApproverName ?? undefined,
                approvedAt: dayjs.tz(approval.createdAt),
              };
            }

            return obj;
          },
          {} as ApprovalByRole<string>,
        );

        setApprovals(approvalsByRole);
      } else {
        setApprovals({});
      }
    } catch (error) {
      setApprovals({});
      connection.handleRequestError(
        error,
        t(
          'error_messages.approvals_assignments.failed_to_fetch_driver_day_approvals_status',
        ) as string,
      );
    } finally {
      setIsLoading(false);
    }
  }, [companyId, driverDayId]);

  useEffect(() => {
    fetchApprovals();
  }, [companyId, driverDayId]);

  return (
    <ApprovalsQueryContext.Provider
      value={{
        approvals,
        isLoading,
        refetchApprovals: fetchApprovals,
      }}
    >
      {children}
    </ApprovalsQueryContext.Provider>
  );
}

export const useApprovalsQueryContext = () => {
  const context = useContext(ApprovalsQueryContext);
  if (context === undefined) {
    throw new Error(
      'useApprovalsQueryContext must be used within an ApprovalsQueryContextProvider',
    );
  }
  return context;
};

export { ApprovalsQueryContextProvider };
